import { memo, useMemo, useState, useEffect, useRef } from "react";
import { useMe, useQueryOptionsSetter, useInfiniteActivities } from "hooks";
import { getGeocode } from "use-places-autocomplete";
import { useIssueList } from "modules/IssueList/IssueList.hooks";
import { useSolutionList } from "modules/SolutionList/SolutionList.hooks";
import { useActionGroupsList } from "modules/GroupList/GroupList.hooks";
import { useGeolocated } from "react-geolocated";
import { useOutletContext, useSearchParams, Link, useNavigate } from "react-router-dom";
import { IOutletAuth, IActivity, IUser } from "types";
import { APP_ROUTES } from "const";
import { differenceInMinutes, format } from "date-fns";
import {
  getActivityNavigation,
  getNotificationDescription,
  useGoogleMaps,
} from "utils";
import AvatarPlaceholder from "assets/images/avatar.png";

// ============================================================================
// DESIGN: "Warm Archival Scrapbook"
// A personal civic journal - intimate, curated, like collecting your journey
// ============================================================================

// Scroll-triggered animation wrapper with staggered reveals
function AnimatedSection({
  children,
  delay = 0,
  className = "",
  direction = "up",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  direction?: "up" | "left" | "right" | "scale";
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  const transforms = {
    up: isVisible ? "translateY(0)" : "translateY(30px)",
    left: isVisible ? "translateX(0)" : "translateX(-30px)",
    right: isVisible ? "translateX(0)" : "translateX(30px)",
    scale: isVisible ? "scale(1)" : "scale(0.95)",
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: transforms[direction],
        transition: "opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1), transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {children}
    </div>
  );
}

// Decorative stamp badge - like a collector's item
function StampBadge({
  number,
  label,
  color,
  rotation = 0,
}: {
  number: number;
  label: string;
  color: string;
  rotation?: number;
}) {
  return (
    <div
      className="relative group cursor-pointer"
      style={{ transform: `rotate(${rotation}deg)` }}
    >
      {/* Outer stamp border */}
      <div
        className="relative w-24 h-24 flex flex-col items-center justify-center transition-transform duration-300 group-hover:scale-110"
        style={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
          border: `2px dashed ${color}`,
          borderRadius: "50%",
        }}
      >
        {/* Inner circle */}
        <div
          className="absolute inset-2 rounded-full"
          style={{ border: `1px solid ${color}40` }}
        />

        {/* Number */}
        <span
          className="text-3xl font-bold"
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            color: color,
          }}
        >
          {number}
        </span>

        {/* Label */}
        <span
          className="text-[9px] font-bold uppercase tracking-widest text-center px-2"
          style={{ color: `${color}CC` }}
        >
          {label}
        </span>
      </div>

      {/* Stamp shadow */}
      <div
        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `${color}10`,
          transform: "translate(4px, 4px)",
          zIndex: -1,
        }}
      />
    </div>
  );
}

// Journal-style activity entry
function JournalEntry({
  activity,
  index,
}: {
  activity: IActivity<IUser>;
  index: number;
}) {
  const navigate = useNavigate();
  const description = getNotificationDescription(activity);
  const timeDiff = Math.abs(differenceInMinutes(new Date(activity.createdAt), new Date()));

  const timeLabel = useMemo(() => {
    if (timeDiff < 60) return `${timeDiff} minutes ago`;
    if (timeDiff < 1440) return `${Math.ceil(timeDiff / 60)} hours ago`;
    if (timeDiff < 10080) return `${Math.ceil(timeDiff / 1440)} days ago`;
    return format(new Date(activity.createdAt), "MMMM d, yyyy");
  }, [timeDiff, activity.createdAt]);

  const handleClick = () => {
    navigate(getActivityNavigation(activity.representEntityType, activity.representEntityId));
  };

  const isEven = index % 2 === 0;

  return (
    <button
      onClick={handleClick}
      className="w-full text-left group"
    >
      <div className="flex gap-4 py-5">
        {/* Timeline dot and line */}
        <div className="flex flex-col items-center">
          <div
            className="w-3 h-3 rounded-full shrink-0 transition-all duration-300 group-hover:scale-150"
            style={{
              background: activity.seen ? "#D6D3D1" : "#C2410C",
              boxShadow: activity.seen ? "none" : "0 0 0 4px rgba(194, 65, 12, 0.15)",
            }}
          />
          <div
            className="w-px flex-1 mt-2"
            style={{ background: "linear-gradient(to bottom, #E7E5E4, transparent)" }}
          />
        </div>

        {/* Content card - alternating slight offset */}
        <div
          className="flex-1 p-4 transition-all duration-300 group-hover:shadow-lg"
          style={{
            background: "#FFFEF9",
            border: "1px solid #E7E5E4",
            borderRadius: "3px",
            transform: isEven ? "rotate(-0.3deg)" : "rotate(0.3deg)",
            boxShadow: "2px 2px 0 #E7E5E4",
          }}
        >
          {/* Timestamp - handwritten style */}
          <p
            className="text-xs mb-2"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontStyle: "italic",
              color: "#A8A29E",
            }}
          >
            {timeLabel}
          </p>

          {/* Title */}
          <p
            className="text-sm font-semibold mb-1 group-hover:text-orange-700 transition-colors"
            style={{ color: "#1C1917" }}
          >
            {activity.title}
          </p>

          {/* Description */}
          <p className="text-sm line-clamp-2" style={{ color: "#78716C" }}>
            {description}
          </p>

          {/* Decorative corner fold */}
          <div
            className="absolute top-0 right-0 w-0 h-0 opacity-0 group-hover:opacity-100 transition-opacity"
            style={{
              borderTop: "12px solid #F5F5F4",
              borderLeft: "12px solid transparent",
            }}
          />
        </div>
      </div>
    </button>
  );
}

// Polaroid-style card for nearby content
function PolaroidCard({
  type,
  title,
  description,
  category,
  rating,
  location,
  href,
  rotation = 0,
}: {
  type: "issue" | "solution";
  title: string;
  description?: string;
  category?: string;
  rating?: number;
  location?: string;
  href: string;
  rotation?: number;
}) {
  const colors = type === "issue"
    ? { primary: "#C2410C", secondary: "#FED7AA", label: "Issue" }
    : { primary: "#059669", secondary: "#A7F3D0", label: "Solution" };

  return (
    <Link
      to={href}
      className="block group transition-all duration-300 hover:z-10"
      style={{
        transform: `rotate(${rotation}deg)`,
      }}
    >
      <div
        className="relative p-4 pb-6 transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-2"
        style={{
          background: "#FFFEF9",
          boxShadow: "3px 3px 0 #D6D3D1, 4px 4px 10px rgba(0,0,0,0.08)",
        }}
      >
        {/* Tape decoration */}
        <div
          className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-4 opacity-60"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,235,200,0.6) 100%)",
            transform: "rotate(-2deg)",
          }}
        />

        {/* Type badge - like a sticker */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1"
            style={{
              background: colors.secondary,
              color: colors.primary,
              transform: "rotate(-1deg)",
              borderRadius: "2px",
            }}
          >
            {colors.label}
          </span>
          {rating !== undefined && rating > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-amber-500">★</span>
              <span className="text-xs font-bold" style={{ color: "#78716C" }}>
                {rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {/* Title - handwritten feel */}
        <h4
          className="text-base font-semibold mb-2 line-clamp-2 group-hover:text-orange-700 transition-colors"
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            color: "#1C1917",
            lineHeight: "1.3",
          }}
        >
          {title}
        </h4>

        {/* Description */}
        {description && (
          <p className="text-xs line-clamp-2 mb-3" style={{ color: "#78716C" }}>
            {description}
          </p>
        )}

        {/* Footer with location */}
        <div className="flex items-center justify-between pt-2" style={{ borderTop: "1px dashed #E7E5E4" }}>
          {category && (
            <span className="text-[10px] uppercase tracking-wide" style={{ color: "#A8A29E" }}>
              {category}
            </span>
          )}
          {location && (
            <span className="text-[10px] flex items-center gap-1" style={{ color: "#A8A29E" }}>
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              {location}
            </span>
          )}
        </div>

        {/* Corner pin decoration */}
        <div
          className="absolute -top-1 -left-1 w-3 h-3 rounded-full"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${colors.primary}, ${colors.primary}99)`,
            boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
          }}
        />
      </div>
    </Link>
  );
}

// Quick action button with vintage feel
function VintageButton({
  to,
  icon,
  label,
  color,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 p-4 transition-all duration-300 hover:-translate-y-1"
      style={{
        background: "#FFFEF9",
        border: "1px solid #E7E5E4",
        borderBottom: `3px solid ${color}`,
        boxShadow: "2px 2px 0 #E7E5E4",
      }}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:rotate-12"
        style={{ background: `${color}15`, color }}
      >
        {icon}
      </div>
      <div>
        <span
          className="block text-sm font-semibold group-hover:text-orange-700 transition-colors"
          style={{ color: "#1C1917" }}
        >
          {label}
        </span>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#A8A29E" }}>
          Click to explore →
        </span>
      </div>
    </Link>
  );
}

function Dashboard() {
  const authContext = useOutletContext<IOutletAuth>();
  const navigate = useNavigate();
  const { data: meData } = useMe();
  const { coords, isGeolocationAvailable, isGeolocationEnabled, getPosition } =
    useGeolocated({
      positionOptions: { enableHighAccuracy: false },
      userDecisionTimeout: 5000,
      suppressLocationOnMount: true,
    });

  const [userLocationCoordinates, setUserLocationCoordinates] = useState({ lat: "", lng: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  useQueryOptionsSetter(searchParams, setSearchParams);
  const placeId = searchParams.get("placeId") || "";
  const mapsLoaded = useGoogleMaps();

  const positionIsFetched = useRef(false);

  // Geolocation effects (unchanged)
  useEffect(() => {
    if (!positionIsFetched.current && (!meData?.data?.lat || !meData?.data?.lng)) {
      if (isGeolocationAvailable) {
        positionIsFetched.current = true;
        getPosition();
      }
    }
  }, [meData?.data?.lng, meData?.data?.lat, isGeolocationAvailable, getPosition]);

  useEffect(() => {
    if (isGeolocationAvailable && isGeolocationEnabled && coords && (!meData?.data?.lat || !meData?.data?.lng) && !placeId) {
      setUserLocationCoordinates({ lat: String(coords.latitude), lng: String(coords.longitude) });
    }
  }, [coords, placeId, isGeolocationAvailable, isGeolocationEnabled, meData?.data?.lng, meData?.data?.lat]);

  useEffect(() => {
    if (!placeId && meData?.data?.lat && meData?.data?.lng) {
      setUserLocationCoordinates({ lat: meData?.data?.lat, lng: meData?.data?.lng });
    }
  }, [placeId, meData?.data?.lat, meData?.data?.lng]);

  useEffect(() => {
    // getGeocode calls into the Maps SDK, which is now loaded on demand.
    if (placeId && mapsLoaded) {
      getGeocode({ placeId }).then((result) => {
        const latlng = result[0].geometry.location;
        setUserLocationCoordinates({ lat: String(latlng?.lat()), lng: String(latlng?.lng()) });
      });
    }
  }, [placeId, mapsLoaded]);

  useEffect(() => {
    if (!authContext.isAuth && (!placeId || placeId.length === 0) && !isGeolocationEnabled) {
      setUserLocationCoordinates({ lat: "", lng: "" });
    }
  }, [placeId, authContext.isAuth, isGeolocationEnabled]);

  // Data fetching - nearby content
  const { data: solutionsData } = useSolutionList(
    { user: "", skills: [], category: [], tags: [], zoom: 9, lat: userLocationCoordinates.lat, lng: userLocationCoordinates.lng },
    1045, 6
  );

  const { data: issuesData } = useIssueList(
    { user: "", skills: [], tags: [], category: [], zoom: 9, lat: userLocationCoordinates.lat, lng: userLocationCoordinates.lng },
    1045, 6
  );

  useActionGroupsList(
    { user: "", skills: [], category: [], tags: [], zoom: 1, lat: "", lng: "" },
    1045
  );

  // User-specific data for stats
  const userId = meData?.data?.id;
  const hasUserId = Boolean(userId);

  const { data: myIssuesData } = useIssueList(
    hasUserId ? { user: userId } : {},
    undefined,
    100,
    !hasUserId
  );

  const { data: mySolutionsData } = useSolutionList(
    hasUserId ? { user: userId } : {},
    undefined,
    100,
    !hasUserId
  );

  const { data: myGroupsData } = useActionGroupsList(
    hasUserId ? { user: userId } : {},
    undefined,
    !hasUserId
  );

  const { data: activitiesData } = useInfiniteActivities({
    representEntityUser: meData?.data.id || "",
  });

  const issues = useMemo(() => issuesData?.pages?.[0]?.data?.items || [], [issuesData]);
  const solutions = useMemo(() => solutionsData?.pages?.[0]?.data?.items || [], [solutionsData]);
  const activities = useMemo(() =>
    activitiesData?.pages?.flatMap(p => p.data.items)?.slice(0, 8) || [],
    [activitiesData]
  );

  // User stats calculation
  const userStats = useMemo(() => {
    if (!userId) return { issues: 0, solutions: 0, groups: 0 };

    const myIssues = myIssuesData?.pages?.flatMap(p => p.data.items) || [];
    const mySolutions = mySolutionsData?.pages?.flatMap(p => p.data.items) || [];
    const myGroups = myGroupsData?.pages?.flatMap(p => p.data.items) || [];

    const isMyIssue = (item: any) => {
      const itemUserId = typeof item.user === 'string' ? item.user : item.user?.id;
      return itemUserId === userId;
    };
    const isMySolution = (item: any) => {
      const itemUserId = typeof item.user === 'string' ? item.user : item.user?.id;
      return itemUserId === userId;
    };
    const isMyGroup = (item: any) => {
      const creatorId = typeof item.user === 'string' ? item.user : item.user?.id;
      const isMember = item.actionGroupUsers?.some((m: any) => {
        const memberId = typeof m.user === 'string' ? m.user : m.user?.id;
        return memberId === userId;
      });
      return creatorId === userId || isMember;
    };

    return {
      issues: myIssues.filter(isMyIssue).length,
      solutions: mySolutions.filter(isMySolution).length,
      groups: myGroups.filter(isMyGroup).length,
    };
  }, [userId, myIssuesData, mySolutionsData, myGroupsData]);

  const user = meData?.data;
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const currentDate = format(new Date(), "EEEE, MMMM d, yyyy");

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#FAF9F7",
        backgroundImage: `
          url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"),
          linear-gradient(180deg, #FAF9F7 0%, #F5F4F1 100%)
        `,
        backgroundBlendMode: "soft-light, normal",
      }}
    >
      {/* ================================================================== */}
      {/* HERO: Personal Journal Header */}
      {/* ================================================================== */}
      <section className="relative overflow-hidden">
        {/* Decorative vintage border */}
        <div
          className="absolute top-0 left-0 right-0 h-2"
          style={{
            background: `repeating-linear-gradient(
              90deg,
              #C2410C 0px,
              #C2410C 20px,
              transparent 20px,
              transparent 25px,
              #C2410C 25px,
              #C2410C 45px,
              transparent 45px,
              transparent 50px
            )`,
            opacity: 0.6,
          }}
        />

        <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-12 pb-8">
          <AnimatedSection>
            {/* Date header - like a journal entry */}
            <p
              className="text-sm uppercase tracking-[0.3em] mb-8"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                color: "#A8A29E",
              }}
            >
              {currentDate}
            </p>

            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
              {/* Avatar with decorative frame */}
              <div className="relative">
                <div
                  className="absolute -inset-3"
                  style={{
                    border: "2px solid #E7E5E4",
                    borderRadius: "4px",
                    transform: "rotate(3deg)",
                  }}
                />
                <div
                  className="absolute -inset-2"
                  style={{
                    border: "1px solid #D6D3D1",
                    borderRadius: "4px",
                    transform: "rotate(-1deg)",
                  }}
                />
                <img
                  src={user?.avatar || AvatarPlaceholder}
                  alt=""
                  className="relative w-24 h-24 lg:w-28 lg:h-28 object-cover"
                  style={{
                    border: "4px solid #FFFEF9",
                    borderRadius: "4px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  }}
                />
                {/* Verified stamp */}
                <div
                  className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: "#C2410C",
                    border: "3px solid #FFFEF9",
                    boxShadow: "0 2px 8px rgba(194, 65, 12, 0.3)",
                  }}
                >
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              {/* Greeting */}
              <div className="flex-1">
                <p
                  className="text-base mb-1"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontStyle: "italic",
                    color: "#78716C",
                  }}
                >
                  {greeting},
                </p>
                <h1
                  className="text-4xl lg:text-5xl font-normal mb-3"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    color: "#1C1917",
                    lineHeight: "1.1",
                  }}
                >
                  {user?.firstName || "Welcome"}
                  {/* Handwritten underline */}
                  <svg
                    className="w-full h-3 mt-1"
                    viewBox="0 0 200 12"
                    fill="none"
                    style={{ maxWidth: "200px" }}
                  >
                    <path
                      d="M2 8 C 30 4, 60 10, 100 6 S 150 8, 198 4"
                      stroke="#C2410C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      fill="none"
                      opacity="0.7"
                    />
                  </svg>
                </h1>
                {user?.primaryLocation?.displayLabel && (
                  <p
                    className="flex items-center gap-2 text-sm"
                    style={{ color: "#78716C" }}
                  >
                    <span style={{ color: "#C2410C" }}>◆</span>
                    {user.primaryLocation.displayLabel}
                  </p>
                )}
              </div>

              {/* Profile link - vintage button style */}
              <Link
                to={APP_ROUTES.PROFILE}
                className="group flex items-center gap-3 px-6 py-3 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: "#1C1917",
                  color: "#FFFEF9",
                  boxShadow: "4px 4px 0 #C2410C",
                }}
              >
                <span className="text-sm font-medium">View Profile</span>
                <svg
                  className="w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================== */}
      {/* COLLECTION: Stamp-style Stats */}
      {/* ================================================================== */}
      <section className="relative py-12 overflow-hidden">
        {/* Section label */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8 mb-8">
          <AnimatedSection>
            <div className="flex items-center gap-4">
              <h2
                className="text-sm uppercase tracking-[0.2em] font-semibold"
                style={{ color: "#78716C" }}
              >
                Your Collection
              </h2>
              <div className="flex-1 h-px" style={{ background: "#E7E5E4" }} />
            </div>
          </AnimatedSection>
        </div>

        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <AnimatedSection delay={100}>
            <div
              className="relative p-8 lg:p-12"
              style={{
                background: "#FFFEF9",
                border: "1px solid #E7E5E4",
                boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              }}
            >
              {/* Corner decorations */}
              <div className="absolute top-4 left-4 w-6 h-6" style={{ borderTop: "2px solid #C2410C", borderLeft: "2px solid #C2410C" }} />
              <div className="absolute top-4 right-4 w-6 h-6" style={{ borderTop: "2px solid #C2410C", borderRight: "2px solid #C2410C" }} />
              <div className="absolute bottom-4 left-4 w-6 h-6" style={{ borderBottom: "2px solid #C2410C", borderLeft: "2px solid #C2410C" }} />
              <div className="absolute bottom-4 right-4 w-6 h-6" style={{ borderBottom: "2px solid #C2410C", borderRight: "2px solid #C2410C" }} />

              <div className="flex flex-wrap justify-center lg:justify-between items-center gap-8 lg:gap-4">
                <button onClick={() => navigate(APP_ROUTES.ISSUES)}>
                  <StampBadge number={userStats.issues} label="Issues" color="#C2410C" rotation={-3} />
                </button>
                <button onClick={() => navigate(APP_ROUTES.SOLUTION_LIST)}>
                  <StampBadge number={userStats.solutions} label="Solutions" color="#059669" rotation={2} />
                </button>
                <button onClick={() => navigate(APP_ROUTES.ACTION_GROUP_LIST)}>
                  <StampBadge number={userStats.groups} label="Groups" color="#7C3AED" rotation={-1} />
                </button>
                <StampBadge number={user?.reputationScore || 0} label="Reputation" color="#0891B2" rotation={3} />
              </div>

              {/* Decorative text */}
              <p
                className="text-center mt-8 text-sm"
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontStyle: "italic",
                  color: "#A8A29E",
                }}
              >
                "Every contribution matters in the journey toward change."
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ================================================================== */}
      {/* MAIN CONTENT: Two Column Layout */}
      {/* ================================================================== */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* ============================================================ */}
          {/* LEFT: Activity Journal (3 cols) */}
          {/* ============================================================ */}
          <div className="lg:col-span-3">
            <AnimatedSection delay={200}>
              {/* Section header */}
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-10 h-10 flex items-center justify-center"
                  style={{
                    background: "#1C1917",
                    boxShadow: "3px 3px 0 #C2410C",
                  }}
                >
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h2
                    className="text-xl font-semibold"
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      color: "#1C1917",
                    }}
                  >
                    Your Journal
                  </h2>
                  <p className="text-xs uppercase tracking-wider" style={{ color: "#A8A29E" }}>
                    Recent activity & updates
                  </p>
                </div>
              </div>

              {/* Journal entries */}
              <div
                className="relative p-6"
                style={{
                  background: "#FFFEF9",
                  border: "1px solid #E7E5E4",
                  borderLeft: "4px solid #C2410C",
                }}
              >
                {/* Notebook holes decoration */}
                <div className="absolute left-0 top-0 bottom-0 w-6 flex flex-col justify-evenly items-center py-8">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-full"
                      style={{ background: "#F5F5F4", border: "1px solid #E7E5E4" }}
                    />
                  ))}
                </div>

                <div className="pl-4">
                  {activities.length > 0 ? (
                    activities.map((activity, i) => (
                      <JournalEntry key={activity.id} activity={activity} index={i} />
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <p
                        className="text-lg mb-2"
                        style={{
                          fontFamily: "'Source Serif 4', Georgia, serif",
                          color: "#1C1917",
                        }}
                      >
                        Your story begins here
                      </p>
                      <p className="text-sm mb-4" style={{ color: "#78716C" }}>
                        Start engaging with issues to fill your journal.
                      </p>
                      <Link
                        to={APP_ROUTES.ISSUES}
                        className="inline-block px-6 py-2 text-sm font-medium transition-all hover:-translate-y-1"
                        style={{
                          background: "#C2410C",
                          color: "#FFFEF9",
                          boxShadow: "3px 3px 0 #1C1917",
                        }}
                      >
                        Explore Issues
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* ============================================================ */}
          {/* RIGHT: Pinboard (2 cols) */}
          {/* ============================================================ */}
          <div className="lg:col-span-2 space-y-8">
            {/* Nearby Issues - Polaroid style */}
            <AnimatedSection delay={300} direction="right">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-lg" style={{ color: "#C2410C" }}>📍</span>
                <h3
                  className="text-base font-semibold"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    color: "#1C1917",
                  }}
                >
                  Issues Near You
                </h3>
              </div>
              <div className="space-y-4">
                {issues.slice(0, 3).map((issue, i) => (
                  <PolaroidCard
                    key={issue.id}
                    type="issue"
                    title={issue.title}
                    description={issue.details}
                    category={issue.primaryClassification?.title}
                    rating={issue.rating}
                    location={issue.location?.country ?? undefined}
                    href={`${APP_ROUTES.ISSUE}/${issue.id}`}
                    rotation={[1.5, -1, 0.5][i] || 0}
                  />
                ))}
                {issues.length === 0 && (
                  <p className="text-sm py-8 text-center" style={{ color: "#78716C" }}>
                    No nearby issues found
                  </p>
                )}
                <Link
                  to={APP_ROUTES.ISSUES}
                  className="block text-center text-sm font-medium py-2 transition-colors hover:text-orange-700"
                  style={{ color: "#C2410C" }}
                >
                  View all issues →
                </Link>
              </div>
            </AnimatedSection>

            {/* Nearby Solutions */}
            <AnimatedSection delay={400} direction="right">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-lg" style={{ color: "#059669" }}>💡</span>
                <h3
                  className="text-base font-semibold"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    color: "#1C1917",
                  }}
                >
                  Solutions Near You
                </h3>
              </div>
              <div className="space-y-4">
                {solutions.slice(0, 2).map((solution, i) => (
                  <PolaroidCard
                    key={solution.id}
                    type="solution"
                    title={solution.title}
                    description={solution.effectiveness}
                    rating={solution.impactRanking}
                    location={solution.issue?.location?.country ?? undefined}
                    href={`${APP_ROUTES.SOLUTION}/${solution.id}`}
                    rotation={[-1, 1.5][i] || 0}
                  />
                ))}
                {solutions.length === 0 && (
                  <p className="text-sm py-8 text-center" style={{ color: "#78716C" }}>
                    No nearby solutions found
                  </p>
                )}
                <Link
                  to={APP_ROUTES.SOLUTION_LIST}
                  className="block text-center text-sm font-medium py-2 transition-colors hover:text-green-700"
                  style={{ color: "#059669" }}
                >
                  View all solutions →
                </Link>
              </div>
            </AnimatedSection>

            {/* Quick Actions */}
            <AnimatedSection delay={500} direction="right">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-lg">⚡</span>
                <h3
                  className="text-base font-semibold"
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    color: "#1C1917",
                  }}
                >
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-3">
                <VintageButton
                  to={APP_ROUTES.ISSUES}
                  color="#C2410C"
                  label="Report an Issue"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  }
                />
                <VintageButton
                  to={APP_ROUTES.ACTION_GROUP_LIST}
                  color="#7C3AED"
                  label="Join a Group"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  }
                />
                <VintageButton
                  to={APP_ROUTES.IMPACT}
                  color="#0891B2"
                  label="See Global Impact"
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ================================================================== */}
      {/* FOOTER: Decorative border */}
      {/* ================================================================== */}
      <div
        className="h-2"
        style={{
          background: `repeating-linear-gradient(
            90deg,
            #C2410C 0px,
            #C2410C 20px,
            transparent 20px,
            transparent 25px,
            #C2410C 25px,
            #C2410C 45px,
            transparent 45px,
            transparent 50px
          )`,
          opacity: 0.6,
        }}
      />
    </div>
  );
}

export default memo(Dashboard);
